import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../../Store/Store';

interface Position {
  latitude: number;
  longitude: number;
}

interface GeoPositionScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

const containerStyle = {
  margin: '0 auto',
  padding: 'clamp(8px, 2vw, 20px)',
  width: '100%',
  maxWidth: '1100px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  boxSizing: 'border-box' as const,
};

const mapContainerStyle = {
  width: '100%',
  minHeight: '150px',
  height: 'clamp(150px, 17vh, 300px)',
  border: '1px solid #ccc',
  borderRadius: '5px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  position: 'relative' as const,
};

const inputStyle = {
  padding: '8px 12px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '14px',
};

const buttonStyle = {
  padding: '12px 24px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 'bold' as const,
  minHeight: '44px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const primaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: '#28a745',
  color: 'white',
};

const secondaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: '#007bff',
  color: 'white',
};

const errorContainerStyle = {
  padding: '10px 40px 10px 10px',
  margin: '10px 0',
  border: '1px solid #ff4444',
  borderRadius: '4px',
  backgroundColor: '#ffe6e6',
  position: 'relative' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const closeButtonStyle = {
  position: 'absolute' as const,
  top: '5px',
  right: '10px',
  background: 'none',
  border: 'none',
  fontSize: '18px',
  fontWeight: 'bold' as const,
  cursor: 'pointer',
  color: '#ff4444',
  padding: '0',
  lineHeight: '1',
  width: '20px',
  height: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Wizard/Modal styles
const wizardOverlayStyle = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const wizardContentStyle = {
  backgroundColor: 'white',
  padding: 'clamp(16px, 4vw, 30px)',
  borderRadius: '12px',
  maxWidth: '600px',
  width: 'min(90%, 600px)',
  maxHeight: '85dvh',
  overflow: 'auto',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  margin: '0 auto',
  boxSizing: 'border-box' as const,
};

const wizardHeaderStyle = {
  fontSize: '18px',
  fontWeight: 'bold' as const,
  marginBottom: '20px',
  textAlign: 'center' as const,
};

const wizardButtonContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: '15px',
  justifyContent: 'center',
  marginTop: '20px',
};

// Error popup styles
const errorPopupOverlayStyle = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2000,
};

const errorPopupStyle = {
  backgroundColor: '#ffe6e6',
  border: '2px solid #ff4444',
  borderRadius: '8px',
  padding: '20px',
  maxWidth: '400px',
  width: '90%',
  textAlign: 'center' as const,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  position: 'relative' as const,
};

const errorPopupButtonStyle = {
  backgroundColor: '#ff4444',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  padding: '10px 20px',
  cursor: 'pointer',
  marginTop: '15px',
  fontSize: '14px',
  fontWeight: 'bold' as const,
};

const GeoPositionScreen: React.FC<GeoPositionScreenProps> = ({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}) => {
  const { product, backendBaseUrl, setGlobalNotification, style, setProduct } =
    useAppContext();
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [manualPosition, setManualPosition] = useState('');
  const [map, setMap] = useState<any>(null);
  const [marker, setMarker] = useState<any>(null);
  const [error, setError] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [address, setAddress] = useState('');
  const [geocoder, setGeocoder] = useState<any>(null);
  const [formattedAddress, setFormattedAddress] = useState('');
  const mapRef = React.useRef<HTMLDivElement | null>(null);

  // Wizard state
  const [activeWizard, setActiveWizard] = useState<
    'none' | 'manualPosition' | 'confirmPosition'
  >('none');

  // Initialize map when position is available
  useEffect(() => {
    if (position && mapRef.current) {
      console.log('Initializing map with position:', position);
      initializeMap(position);
    }
  }, [position]);

  // Initialize geocoder when component mounts
  useEffect(() => {
    if (window.google && window.google.maps) {
      console.log('Google Maps loaded successfully');
      const geocoderInstance = new window.google.maps.Geocoder();
      setGeocoder(geocoderInstance);
    }
  }, []);

  // Request user location on component mount
  useEffect(() => {
    requestUserLocation();
  }, []);

  const requestUserLocation = async () => {
    if (!navigator.geolocation) {
      showError('Geolocation is not supported by this browser');
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    try {
      console.log('Requesting user location...');
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, options);
        }
      );

      const newPosition = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      console.log('Position revealed for the user:', newPosition);
      setPosition(newPosition);
    } catch (error) {
      console.error('Error getting location:', error);
      showError('Unable to get your location. Please enter it manually.');
    }
  };

  const initializeMap = (center: Position) => {
    if (!window.google) {
      console.error('Google Maps not loaded');
      showError('Google Maps not loaded');
      return;
    }

    console.log('Initializing map with center:', center);

    const mapElement = mapRef.current;
    if (!mapElement) {
      console.error('Map element not found');
      return;
    }

    const mapInstance = new window.google.maps.Map(mapElement, {
      zoom: 15,
      center: { lat: center.latitude, lng: center.longitude },
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
    });

    const markerInstance = new window.google.maps.Marker({
      position: { lat: center.latitude, lng: center.longitude },
      map: mapInstance,
      draggable: false,
      title: 'Geo Position',
    });

    // Add click listener to map
    mapInstance.addListener('click', (event: any) => {
      const clickedPosition = {
        latitude: event.latLng.lat(),
        longitude: event.latLng.lng(),
      };

      // Update position
      setPosition(clickedPosition);

      // Move marker to new position
      markerInstance.setPosition(event.latLng);

      // Center map on new position
      mapInstance.setCenter(event.latLng);

      console.log('Map clicked, new position:', clickedPosition);
    });

    setMap(mapInstance);
    setMarker(markerInstance);
    console.log('Map initialized successfully with click functionality');
  };

  const handleAddressSearch = () => {
    if (!geocoder || !address) return;

    geocoder.geocode({ address }, (results: any, status: any) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const newPosition = {
          latitude: location.lat(),
          longitude: location.lng(),
        };
        setPosition(newPosition);
        setActiveWizard('none'); // Close wizard after successful address search
      } else {
        showError('Address not found. Please try again.');
      }
    });
  };

  const handleManualPositionSubmit = () => {
    try {
      const coords = manualPosition
        .split(',')
        .map(coord => parseFloat(coord.trim()));
      if (coords.length !== 2 || coords.some(isNaN)) {
        showError(
          'Please enter valid coordinates in format: latitude, longitude'
        );
        return;
      }

      const [latitude, longitude] = coords;
      if (
        latitude < -90 ||
        latitude > 90 ||
        longitude < -180 ||
        longitude > 180
      ) {
        showError(
          'Please enter valid coordinates (latitude: -90 to 90, longitude: -180 to 180)'
        );
        return;
      }

      setPosition({ latitude, longitude });
      setActiveWizard('none'); // Close wizard after successful manual position set
    } catch (error) {
      showError('Invalid coordinates format');
    }
  };

  const confirmPosition = async () => {
    if (!position) {
      showError('Please select a position');
      return;
    }

    try {
      setLoading(true);
      setActiveWizard('none'); // Close wizard

      // Get address from coordinates
      const address = await getAddressFromCoordinates(
        position.latitude,
        position.longitude
      );

      const response = await axios.post(
        `${backendBaseUrl}/update-geo-position/`,
        {
          product_code: product.code,
          position: `${position.latitude},${position.longitude}`,
          installation_date: new Date().toISOString(),
          address: address, // Include address in the request
        }
      );

      if (response.data.success) {
        // Update the product data in the frontend context with the new geo attributes
        const updatedProduct = {
          ...product,
          geo_attributes: {
            ...product.geo_attributes,
            position: `${position.latitude},${position.longitude}`,
            installation_date: new Date().toISOString(),
            address: address,
          },
        };
        setProduct(updatedProduct);

        onNext(); // Proceed to next screen
      }
    } catch (error: any) {
      showError(
        error.response?.data?.error || 'Failed to register geo position'
      );
    } finally {
      setLoading(false);
    }
  };

  // Get formatted address from coordinates
  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    if (!geocoder) return '';

    try {
      const result = await new Promise<any>((resolve, reject) => {
        geocoder.geocode(
          { location: { lat, lng } },
          (results: any, status: any) => {
            if (status === 'OK' && results && results.length > 0) {
              // Filter out Plus Codes and find a proper street address
              const properAddress = results.find((result: any) => {
                const address = result.formatted_address;
                // Check if it's not a Plus Code (contains +) and has street number/name
                return (
                  !address.includes('+') &&
                  (result.types.includes('street_address') ||
                    result.types.includes('premise') ||
                    result.types.includes('subpremise'))
                );
              });

              if (properAddress) {
                resolve(properAddress);
              } else {
                // If no proper street address, try to find one with route info
                const routeAddress = results.find(
                  (result: any) =>
                    !result.formatted_address.includes('+') &&
                    result.types.includes('route')
                );
                resolve(routeAddress || results[0]);
              }
            } else {
              reject(new Error('Geocoding failed'));
            }
          }
        );
      });

      // Additional filtering to clean up the address
      let address = result.formatted_address;

      // Remove Plus Codes if they somehow made it through
      if (address.includes('+')) {
        // Try to extract just the city and country part
        const parts = address.split(',').map((part: string) => part.trim());
        const cleanParts = parts.filter((part: string) => !part.includes('+'));
        address = cleanParts.length > 0 ? cleanParts.join(', ') : '';
      }

      return address;
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return '';
    }
  };

  // Wizard handlers
  const openManualPositionWizard = () => {
    setActiveWizard('manualPosition');
  };

  const openConfirmPositionWizard = () => {
    if (!position) {
      setError('Please select a position');
      return;
    }
    setActiveWizard('confirmPosition');

    // Get formatted address for the position
    if (geocoder && position) {
      getAddressFromCoordinates(position.latitude, position.longitude)
        .then(address => {
          setFormattedAddress(address);
        })
        .catch(error => {
          console.error('Failed to get address:', error);
          setFormattedAddress('');
        });
    }
  };

  const closeWizard = () => {
    setActiveWizard('none');
  };

  const handleConfirmPositionFromWizard = () => {
    confirmPosition();
  };

  // Calculate responsive button styles based on viewport height
  const getResponsiveButtonStyles = () => {
    const viewportHeight = window.innerHeight;
    const maxAllowedHeight = viewportHeight * 0.33; // 33vh

    // Estimate button container height (buttons + gaps + margins)
    // Base: 2 buttons (~44px each) + gap (15px) + margins (36px) + padding = ~139px
    const estimatedHeight = 139;

    if (estimatedHeight > maxAllowedHeight) {
      // Reduce styles when container would exceed 33vh
      const scaleFactor = Math.max(0.6, maxAllowedHeight / estimatedHeight);
      return {
        padding: `${Math.max(6, 10 * scaleFactor)}px ${Math.max(10, 20 * scaleFactor)}px`,
        fontSize: `${Math.max(10, 13 * scaleFactor)}px`,
        gap: `${Math.max(8, 15 * scaleFactor)}px`,
        marginBottom: `${Math.max(8, 18 * scaleFactor)}px`,
        minHeight: `${Math.max(34, 44 * scaleFactor)}px`,
      };
    }

    // Default styles when there's enough space
    return {
      padding: '10px 20px',
      fontSize: '13px',
      gap: '15px',
      marginBottom: '18px',
      minHeight: '44px',
    };
  };

  const responsiveStyles = getResponsiveButtonStyles();

  // Function to show error popup
  const showError = (message: string) => {
    setError(message);
    setShowErrorPopup(true);
  };

  // Function to close error popup
  const closeErrorPopup = () => {
    setShowErrorPopup(false);
    setError('');
  };

  return (
    <>
      <div style={containerStyle}>
        <div style={{ marginTop: '-40px' }}>
          <div style={{ marginBottom: '2px' }}>
            <p></p>
            <div ref={mapRef} style={mapContainerStyle}></div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              marginBottom: responsiveStyles.marginBottom,
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto',
              maxHeight: '33dvh',
              overflow: 'hidden',
            }}
          >
            {/* Buttons aligned in the same row */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: responsiveStyles.gap,
                justifyContent: 'center',
                alignItems: 'stretch',
              }}
            >
              {/* Only show manual position button if at least one option is enabled */}
              {(style.geoAttributes.geo_position_show_coordinates_option ||
                style.geoAttributes.geo_position_show_street_option) && (
                <button
                  onClick={openManualPositionWizard}
                  style={{
                    ...buttonStyle,
                    backgroundColor:
                      style.geoAttributes.geo_position_manual_button_bg_color ||
                      '#6c757d',
                    color:
                      style.geoAttributes
                        .geo_position_manual_button_text_color || 'white',
                    padding: responsiveStyles.padding,
                    fontSize: responsiveStyles.fontSize,
                    flex: '1 1 140px',
                    minWidth: '0',
                    boxShadow:
                      '0 5px 5px ' +
                      (style.geoAttributes
                        .geo_position_manual_button_shadow_color ||
                        'transparent'),
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      style.geoAttributes
                        .geo_position_manual_button_indication ||
                      'Set position manually',
                  }}
                />
              )}

              <button
                onClick={openConfirmPositionWizard}
                disabled={!position || loading}
                style={{
                  ...buttonStyle,
                  backgroundColor:
                    style.geoAttributes.geo_position_confirm_button_bg_color ||
                    '#07296A',
                  color:
                    style.geoAttributes
                      .geo_position_confirm_button_text_color || 'white',
                  padding: responsiveStyles.padding,
                  fontSize: responsiveStyles.fontSize,
                  flex: '1',
                  cursor: position ? 'pointer' : 'not-allowed',
                  boxShadow:
                    '0 5px 5px ' +
                    (style.geoAttributes
                      .geo_position_confirm_button_shadow_color ||
                      'transparent'),
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    style.geoAttributes
                      .geo_position_confirm_button_indication ||
                    (loading ? 'Saving...' : 'Confirm Position'),
                }}
              />
            </div>
          </div>
          {showBackButton && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '20px',
              }}
            >
              <button
                type="button"
                onClick={() => onBack?.()}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.back_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: style?.back_button_indication || 'Back',
                  }}
                />
              </button>
            </div>
          )}
          {showSkipNextScreenButton && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '20px',
              }}
            >
              <button
                type="button"
                onClick={onNext}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.skip_next_screen_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: style?.skip_next_screen_button_indication || 'Skip',
                  }}
                />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Wizards */}
      {activeWizard === 'manualPosition' && (
        <div
          style={wizardOverlayStyle}
          onClick={e => e.target === e.currentTarget && closeWizard()}
        >
          <div style={wizardContentStyle}>
            <div style={wizardHeaderStyle}>Set Position Manually</div>

            {/* Street Address Search - only show if enabled */}
            {style.geoAttributes.geo_position_show_street_option && (
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                  }}
                >
                  Search by Address:
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    alignItems: 'center',
                    marginBottom: '20px',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Enter address"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    style={{
                      ...inputStyle,
                      flex: '1 1 200px',
                      minWidth: '0',
                      padding: '10px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    onClick={handleAddressSearch}
                    style={{
                      ...secondaryButtonStyle,
                      padding: '10px 16px',
                      fontSize: '14px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                    }}
                  >
                    Search
                  </button>
                </div>
              </div>
            )}

            {/* Coordinates Input - only show if enabled */}
            {style.geoAttributes.geo_position_show_coordinates_option && (
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                  }}
                >
                  {style.geoAttributes.geo_position_show_street_option
                    ? 'Or Enter Coordinates:'
                    : 'Enter Coordinates:'}
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    alignItems: 'center',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Latitude, Longitude (e.g., 45.4642, 9.1900)"
                    value={manualPosition}
                    onChange={e => setManualPosition(e.target.value)}
                    style={{
                      ...inputStyle,
                      flex: '1 1 200px',
                      minWidth: '0',
                      padding: '10px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    onClick={handleManualPositionSubmit}
                    style={{
                      ...secondaryButtonStyle,
                      padding: '10px 16px',
                      fontSize: '14px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                    }}
                  >
                    Set
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div style={errorContainerStyle}>
                <span>{error}</span>
                <button onClick={() => setError('')} style={closeButtonStyle}>
                  &times;
                </button>
              </div>
            )}

            <div style={wizardButtonContainerStyle}>
              <button
                onClick={closeWizard}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#6c757d',
                  color: 'white',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {activeWizard === 'confirmPosition' && (
        <div
          style={wizardOverlayStyle}
          onClick={e => e.target === e.currentTarget && closeWizard()}
        >
          <div style={wizardContentStyle}>
            <div
              style={wizardHeaderStyle}
              dangerouslySetInnerHTML={{
                __html:
                  style.geoAttributes.geo_position_confirm_wizard_indication ||
                  'Conferma di voler registrare la valvola a questa posizione?',
              }}
            />

            {position && (
              <div
                style={{
                  marginBottom: '20px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                {formattedAddress ? (
                  <>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#333',
                        marginBottom: '10px',
                        lineHeight: '1.4',
                      }}
                    >
                      ~ {formattedAddress}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#666',
                        fontFamily: 'monospace',
                      }}
                    >
                      {position.latitude.toFixed(6)},{' '}
                      {position.longitude.toFixed(6)}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#333',
                      fontFamily: 'monospace',
                    }}
                  >
                    {position.latitude.toFixed(6)},{' '}
                    {position.longitude.toFixed(6)}
                  </div>
                )}
              </div>
            )}

            <div style={wizardButtonContainerStyle}>
              <button
                onClick={closeWizard}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#6c757d',
                  color: 'white',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPositionFromWizard}
                disabled={loading}
                style={{
                  ...buttonStyle,
                  backgroundColor:
                    style.geoAttributes.geo_position_confirm_button_bg_color ||
                    '#07296A',
                  color:
                    style.geoAttributes
                      .geo_position_confirm_button_text_color || 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div
          style={errorPopupOverlayStyle}
          onClick={e => e.target === e.currentTarget && closeErrorPopup()}
        >
          <div style={errorPopupStyle}>
            <div
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '10px',
                color: '#ff4444',
              }}
            >
              Error
            </div>
            <div style={{ marginBottom: '15px', color: '#333' }}>{error}</div>
            <button onClick={closeErrorPopup} style={errorPopupButtonStyle}>
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GeoPositionScreen;
